import React, { useEffect, useState } from 'react';
import {
  onSessionConflict,
  hasSessionConflictPending,
  clearSessionConflictPending,
} from '../services/sessionConflictEvent';
import { clearAuthAndRedirectToLogin } from '../services/authNavigation';

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(10, 10, 20, 0.82)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    animation: 'jsyog-fade-in 0.35s ease',
  },
  card: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '20px',
    padding: '48px 40px 40px',
    maxWidth: '460px',
    width: '90%',
    boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,120,80,0.25)',
    textAlign: 'center',
    animation: 'jsyog-slide-up 0.4s cubic-bezier(0.22,1,0.36,1)',
    position: 'relative',
    overflow: 'hidden',
  },
  glow: {
    position: 'absolute',
    top: '-60px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '200px',
    height: '200px',
    background: 'radial-gradient(circle, rgba(255,80,50,0.35) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  iconWrap: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'rgba(255,80,50,0.15)',
    border: '2px solid rgba(255,80,50,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  icon: {
    fontSize: '32px',
    lineHeight: 1,
  },
  title: {
    color: '#fff',
    fontSize: '22px',
    fontWeight: 700,
    margin: '0 0 12px',
    fontFamily: "'Inter', 'Outfit', system-ui, sans-serif",
    letterSpacing: '-0.3px',
  },
  message: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: '15px',
    lineHeight: 1.65,
    margin: '0 0 32px',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  okButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    background: 'linear-gradient(135deg, #ff6b50 0%, #ff8c42 100%)',
    border: 'none',
    borderRadius: '12px',
    padding: '14px 40px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: "'Inter', system-ui, sans-serif",
    letterSpacing: '0.2px',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(255,107,80,0.4)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease',
    outline: 'none',
  },
};

// Inject keyframe animations once
let animationsInjected = false;
function injectAnimations() {
  if (animationsInjected || typeof document === 'undefined') return;
  animationsInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes jsyog-fade-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes jsyog-slide-up {
      from { opacity: 0; transform: translateY(32px) scale(0.96); }
      to   { opacity: 1; transform: translateY(0)   scale(1); }
    }
    #jsyog-ok-btn:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 8px 28px rgba(255,107,80,0.55) !important;
    }
    #jsyog-ok-btn:active {
      transform: translateY(0) !important;
      opacity: 0.85 !important;
    }
  `;
  document.head.appendChild(style);
}

export function SessionConflictBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    injectAnimations();

    // ✅ Restore conflict state from localStorage on mount — survives page refresh.
    // Device 1 will keep seeing the popup even after reloading until OK is clicked.
    if (hasSessionConflictPending()) {
      setVisible(true);
    }

    const unsubscribe = onSessionConflict(() => {
      setVisible(true);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  function handleOk() {
    clearSessionConflictPending();
    clearAuthAndRedirectToLogin('device_conflict');
  }

  if (!visible) return null;

  return (
    <div style={styles.overlay} role="alertdialog" aria-modal="true" aria-live="assertive">
      <div style={styles.card}>
        {/* Glow effect */}
        <div style={styles.glow} />

        {/* Icon */}
        <div style={styles.iconWrap}>
          <span style={styles.icon}>🔒</span>
        </div>

        {/* Title */}
        <h2 style={styles.title}>Session Conflict Detected</h2>

        {/* Message */}
        <p style={styles.message}>
          Your account has been logged in on{' '}
          <strong style={{ color: '#fff' }}>another device</strong>.
          <br />
          For your security, this session will be logged out.
          <br />
          Please click <strong style={{ color: '#ff8c42' }}>OK</strong> to continue.
        </p>

        {/* OK button — no auto-redirect, user must acknowledge */}
        <button id="jsyog-ok-btn" style={styles.okButton} onClick={handleOk} autoFocus>
          OK, Log Me Out
        </button>
      </div>
    </div>
  );
}

export default SessionConflictBanner;
